import { ReactNode, useEffect } from 'react'

import PrintCover from './print-cover'
import RichText from '@/components/ui/rich-text'
import LogoHorizontal from '@/icons/logo-horizontal.svg'

import { cx } from '@/utils/strings'
import { useTranslations } from '@/utils/locale'
import { hideKetchBannerInPrintMode } from '@/utils/print'
import '../../css/print-wrapper.css'

export interface WrapperProps {
  title?: string
  description?: any
  section?: string
  thirdPartyLogo?: any
  pageUrl?: string
  tagline?: string
  coverImage?: any
  pageType?: string
  pageBreadcrumb?: { title: string }[]
  orientation?: string
  securityPerspectives?: boolean
}

export default function PrintWrapper({
  title = '',
  description = '',
  tagline,
  section,
  children,
  thirdPartyLogo,
  pageUrl,
  coverImage,
  pageType,
  pageBreadcrumb,
  orientation = 'portrait',
  securityPerspectives = false,
}: WrapperProps & { children: ReactNode }) {
  const { t } = useTranslations()
  const descStyle = 'relative ml-1 mt-1 mb-1 z-10 text-[16pt] leading-tight [&*]:font-normal! [&*]:text-[16pt]'
  const descStyleWhite = 'text-white [&*]:text-white!'
  const descStyleBlack = 'text-black/80 [&*]:text-black/80!'

  useEffect(() => {
    hideKetchBannerInPrintMode()
  }, [])

  const renderDescriptionText = (descriptionText: any, color: 'white' | 'black' = 'black') =>
    typeof descriptionText === 'string' ? (
      <p className={cx(descStyle, color === 'white' ? descStyleWhite : descStyleBlack)}>{descriptionText}</p>
    ) : (
      <div className={cx(descStyle, color === 'white' ? descStyleWhite : descStyleBlack)}>
        <RichText body={descriptionText} />
      </div>
    )

  const table = (
    <table className="p-0 w-full">
      {/* create space for header */}
      <thead>
        <tr>
          <td className="p-0">
            <div className="h-[50px]" />
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="p-0">
            <div>
              {/* specific x padding to match header and footer of pdf generation */}
              <article id="print-body">{children}</article>
            </div>
          </td>
        </tr>
      </tbody>
      {/* create space for footer */}
      <tfoot>
        <tr>
          <td className="p-0">
            <div className="h-[50px]" />
          </td>
        </tr>
      </tfoot>
    </table>
  )

  if (securityPerspectives) {
    return (
      <>
        <div className="bg-white max-w-7xl mx-auto h-[calc(100vh)] [page-break-after: always]">
          <div className="p-14 bg-primaryBlue relative h-[100px] rounded-b-4xl z-10">
            <div className="text-white fill-current flex items-center z-10">
              <LogoHorizontal className={cx('w-48 flex-[1 0 auto]', thirdPartyLogo && 'border-r-2 pr-7 py-2 mr-7')} />
            </div>
          </div>
          <div className="h-[25px]" />
          <div
            className="h-[488px] bg-white rounded-4xl"
            style={{
              backgroundImage: coverImage?.fields.file.url ? `url(${coverImage.fields.file.url})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="h-[25px]" />
          <div className="h-[calc(100vh-650px)] bg-primaryBlue rounded-t-4xl px-16 pt-12">
            <h2 className="tracking-[0.2em] text-base! text-white font-normal! uppercase header-gradient-reset">
              Bitwarden Security Perspectives
            </h2>

            <h1 className="relative! text-[40pt] leading-tight font-normal text-white header-gradient-reset z-10! w-[80%] mt-0!">
              {title}
            </h1>
            {description && renderDescriptionText(description, 'white')}
          </div>
        </div>
        {table}
      </>
    )
  } else if (pageType === 'help') {
    return (
      <>
        <PrintCover
          title={title || ''}
          section={t('Help Center Article')}
          pageUrl={pageUrl}
          pageBreadcrumb={pageBreadcrumb}
          linkCtaText={t('View in the help center')}
          backgroundClass="gradient-curve-base gradient-curve-1 opacity-80"
        />
        {table}
      </>
    )
  } else {
    return (
      <>
        {orientation === 'landscape' && <style>{`@media print {@page {size: landscape}}`}</style>}
        <PrintCover
          title={title}
          tagline={tagline}
          description={description ? renderDescriptionText(description) : undefined}
          section={
            thirdPartyLogo ? (
              <img
                src={thirdPartyLogo?.fields.file.url}
                alt={thirdPartyLogo?.fields.title}
                className="h-[40pt] object-contain"
              />
            ) : section ? (
              section
            ) : undefined
          }
          pageUrl={pageUrl}
          pageBreadcrumb={pageBreadcrumb}
          linkCtaText={t('View the full interactive version at:')}
          backgroundClass="gradient-curve-base gradient-curve-2 opacity-80"
        />
        {table}
      </>
    )
  }
}
